<template>
  <div class="d-flex">
    <navigator class="navigator" :flag="navFlag" />

    <div class="user-profile-page">
      <div>{{ updateColor() }}</div>

      <!-- Image cropper overlay -->
      <imageCropper
        id="profile-image-cropper"
        v-if="cropperOn"
        :img="imgFile.src"
        :imgType="imgFile.type"
        stencilComponent="circle-stencil"
        :cropperProps="{ aspectRatio: 1 / 1 }"
        @cropConfirmed="handleCropperConfirm"
        @cropCanceled="handleCropperCancel"
      />

      <!-- ── Avatar card ── -->
      <div class="profile-card avatar-card">
        <div
          class="avatar"
          @mouseover="showShade"
          @mouseleave="hideShade"
          :style="{ backgroundImage: 'url(' + userProfile.img + ')', backgroundSize: 'cover' }"
        >
          <input
            id="profile-upload-button"
            v-show="shadeOn"
            type="file"
            ref="file"
            @change="uploadImg($event)"
            accept="image/*"
          />
          <div class="avatar-shade" v-if="shadeOn">
            <span class="shade-label">Change Photo</span>
          </div>
        </div>
        <div class="avatar-info">
          <div class="avatar-name">{{ userProfile.nickName }}</div>
          <div class="avatar-sub" v-if="userProfile.userName">{{ userProfile.userName }}</div>
        </div>
      </div>

      <!-- ── Profile / router-view card ── -->
      <div class="profile-card content-card">
        <router-view
          :userProfile="userProfile"
          :titleList="titleList"
          @changed="handleChange"
          @modifying="handleModify"
          @confirmed="handleConfirm"
        />
      </div>

      <!-- ── Identifiers card (testers only) ── -->
      <div class="profile-card identifiers-card" v-if="userType === 0">
        <div class="card-header">
          <h3 class="card-title">Your Identifiers</h3>
          <button class="modify-btn" @click="handleFiltersNavigation">Modify</button>
        </div>

        <div class="identifier-tags" v-if="displayedTags.length > 0">
          <span
            v-for="(tag, index) in displayedTags"
            :key="index"
            class="identifier-tag"
            :style="{ backgroundColor: tag.color }"
          >{{ tag.value }}</span>
          <span v-if="remainingTagsCount > 0" class="more-tags">
            +{{ remainingTagsCount }} more…
          </span>
        </div>

        <div v-else class="no-identifiers">
          No identifiers set up yet. Click "Modify" to add your testing preferences!
        </div>
      </div>

    </div><!-- /.user-profile-page -->
  </div>
</template>

<script>
import navigator        from '../layout/NavBarNew.vue'
import sidebar          from '../layout/SideBar.vue'
import modifydisplay    from './ModifyDisplay.vue'
import imageCropper     from '../layout/ImageCropper.vue'
import Loading          from '../loading/index.vue'
import UserObject       from '../../Firestore/UserObject'
import PlayTesterUser   from '../../Firestore/PlayTesterUser'

function getMimeType(file, fallback = null) {
  const byteArray = (new Uint8Array(file)).subarray(0, 4);
  let header = '';
  for (let i = 0; i < byteArray.length; i++) header += byteArray[i].toString(16);
  switch (header) {
    case '89504e47': return 'image/png';
    case '47494638': return 'image/gif';
    case 'ffd8ffe0': case 'ffd8ffe1': case 'ffd8ffe2':
    case 'ffd8ffe3': case 'ffd8ffe8': return 'image/jpeg';
    default: return fallback;
  }
}

export default {
  name: 'userprofile',
  components: { navigator, sidebar, imageCropper, modifydisplay, Loading },
  data() {
    return {
      navFlag: -1,
      userType: -1,
      shadeOn: false,
      cropperOn: false,
      isLoading: false,
      imgFile: { src: null, type: null },
      userProfile: {
        userName: '', nickName: '', gender: '', bio: '',
        email: '', workPhone: '', mobile: '', password: '', img: '',
      },
      buffer: {
        userName: '', nickName: '', gender: '', bio: '',
        email: '', workPhone: '', mobile: '', password: '', img: '',
      },
      titleList: {
        userName: 'Name', nickName: 'Nick Name', bio: 'Bio', gender: 'Gender',
        email: 'Email', workPhone: 'Work Phone', mobile: 'Mobile', password: 'Password',
      },
      testerData: {
        age: [], gender: [], hours: [], platform: [],
        behavior: [], genre: [], profile: [], purchasingBehavior: []
      },
    };
  },

  mounted() {
    if (window.localStorage.getItem('userType')) {
      this.userType = parseInt(window.localStorage.getItem('userType'));
      this.isLoading = true;

      UserObject.getProfile(window.localStorage.getItem('userId'))
        .then(res => {
          const d = res.data;
          Object.keys(d).forEach(k => { this.userProfile[k] = d[k]; });
          Object.keys(this.userProfile).forEach(k => { this.buffer[k] = this.userProfile[k]; });
        })
        .catch(e => console.log(e));

      if (this.userType === 0) this.loadTesterData();
      this.isLoading = false;
    } else {
      this.$router.push({ name: 'login' });
    }
  },

  computed: {
    displayedTags() {
      const allTags = [];
      const fieldConfig = [
        { key: 'platform',          color: '#2196F3' },
        { key: 'behavior',          color: '#4CAF50' },
        { key: 'genre',             color: '#F44336' },
        { key: 'profile',           color: '#FFEB3B' },
        { key: 'purchasingBehavior',color: '#00BCD4' },
      ];
      fieldConfig.forEach(f => {
        const data = this.testerData[f.key];
        if (Array.isArray(data) && data.length) {
          data.slice(0, f.key === 'platform' || f.key === 'behavior' ? 2 : 1)
              .forEach(v => allTags.push({ value: v, color: f.color, field: f.key }));
        }
      });
      return allTags.slice(0, 4);
    },
    remainingTagsCount() {
      const important = ['platform', 'behavior', 'genre', 'profile', 'purchasingBehavior'];
      const total = important.reduce((sum, k) => {
        const d = this.testerData[k];
        return sum + (Array.isArray(d) ? d.length : 0);
      }, 0);
      return Math.max(0, total - 4);
    },
  },

  methods: {
    showShade() { this.shadeOn = true; },
    hideShade()  { this.shadeOn = false; },

    uploadImg(event) {
      const { files } = event.target;
      if (files && files[0]) {
        if (this.imgFile.src) URL.revokeObjectURL(this.imgFile.src);
        const blob = URL.createObjectURL(files[0]);
        const reader = new FileReader();
        reader.onload = e => {
          this.imgFile = { src: blob, type: getMimeType(e.target.result, files[0].type) };
        };
        reader.readAsArrayBuffer(files[0]);
      }
      this.cropperOn = true;
    },

    async handleCropperConfirm(imgBlob) {
      this.cropperOn = false;
      const reader = new FileReader();
      reader.readAsDataURL(imgBlob);
      reader.onload = () => {
        this.userProfile.img = reader.result;
        UserObject.updateProfile(
          window.localStorage.getItem('userId'),
          this.userProfile.userName, this.userProfile.nickName,
          this.userProfile.gender,   this.userProfile.bio,
          this.userProfile.email,    this.userProfile.workPhone,
          this.userProfile.mobile,
          this.userProfile.img,
        ).catch(e => console.log(e));
      };
    },

    handleCropperCancel() { this.cropperOn = false; },

    handleChange(title, newContent) {
      const key = Object.keys(this.titleList).find(k => this.titleList[k] === title);
      this.buffer[key] = newContent;
    },
    handleModify() {
      Object.keys(this.userProfile).forEach(k => { this.buffer[k] = this.userProfile[k]; });
    },
    handleConfirm() {
      Object.keys(this.buffer).forEach(k => { this.userProfile[k] = this.buffer[k]; });
      this.isLoading = true;
      UserObject.updateProfile(
        window.localStorage.getItem('userId'),
        this.userProfile.userName, this.userProfile.nickName,
        this.userProfile.gender,   this.userProfile.bio,
        this.userProfile.email,    this.userProfile.workPhone,
        this.userProfile.mobile,
        this.userProfile.img,
      ).then(() => { this.isLoading = false; })
       .catch(e => { console.log(e); this.isLoading = false; });
    },

    updateColor() {
      const r = document.querySelector(':root');
      r.style.setProperty('--USERPROFILE', this.$store.state.userprofile);
      r.style.setProperty('--NICKNAME',    this.$store.state.nickname);
    },

    loadTesterData() {
      PlayTesterUser.getPlayTesterProfile(window.localStorage.getItem('userId'))
        .then(res => {
          const d = res.data;
          this.testerData = {
            age:               Array.isArray(d.age)               ? d.age               : d.age               ? [d.age]               : [],
            gender:            Array.isArray(d.gender)            ? d.gender            : d.gender            ? [d.gender]            : [],
            hours:             Array.isArray(d.hours)             ? d.hours             : d.hours             ? [d.hours]             : [],
            platform:          Array.isArray(d.platform)          ? d.platform          : [],
            behavior:          Array.isArray(d.behavior)          ? d.behavior          : [],
            genre:             Array.isArray(d.genre)             ? d.genre             : [],
            profile:           Array.isArray(d.profile)           ? d.profile           : [],
            purchasingBehavior:Array.isArray(d.purchasingBehavior)? d.purchasingBehavior: [],
          };
        })
        .catch(err => {
          if (err.error !== PlayTesterUser.PROFILE_DNE) console.log('Unexpected error:', err);
        });
    },

    handleFiltersNavigation() {
      this.$router.push({ name: 'playtesteratt' });
    },
  },

  destroyed() {
    if (this.imgFile.src) URL.revokeObjectURL(this.imgFile.src);
  },
};
</script>

<style scoped>
/* ── Shell ── */
.d-flex {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
}

.user-profile-page {
  flex: 1;
  min-width: 0;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;

  /* Single centred column — matches UserProfile-Profile layout */
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 20px 40px;
  gap: 20px;
  box-sizing: border-box;
}

/* ── Shared card shell — mirrors .profile-main-content-centered ── */
.profile-card {
  width: 90%;
  max-width: 1200px;
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 6px 6px 20px rgba(255, 180, 80, 0.15);
  box-sizing: border-box;
}

/* ── Avatar card ── */
.avatar-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  padding: 24px 32px;
  background-image: url('../../../src/assets/HorizontalGradientLogo-17.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 55%;
  background-color: #ffffff;
}

.avatar {
  position: relative;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: #eeeeee;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(255, 114, 34, 0.25);
  flex-shrink: 0;
}

#profile-upload-button {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 4;
  width: 100%;
  height: 100%;
}

.avatar-shade {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.shade-label {
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
  pointer-events: none;
}

.avatar-info {
  text-align: left;
}

.avatar-name {
  font-size: 22px;
  font-weight: 700;
  color: var(--NICKNAME, #333);
}

.avatar-sub {
  font-size: 14px;
  color: #888;
  margin-top: 4px;
}

/* ── Content card (router-view) ── */
.content-card {
  padding: 0; /* UserProfile-Profile.vue has its own internal padding */
  overflow: visible;
}

/* ── Identifiers card ── */
.identifiers-card {
  padding: 28px 30px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-title {
  color: #fc7727;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.modify-btn {
  background: #ff7222;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
}
.modify-btn:hover {
  background: #ff8c00;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 127, 0, 0.3);
}

.identifier-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.identifier-tag {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 25px;
  color: white;
  font-weight: 500;
  font-size: 14px;
  min-width: 100px;
  text-align: center;
  transition: transform 0.15s, box-shadow 0.15s;
}
.identifier-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.more-tags {
  color: #888;
  font-size: 14px;
  font-style: italic;
  margin-left: 6px;
}

.no-identifiers {
  color: #888;
  font-style: italic;
  font-size: 15px;
  text-align: center;
  padding: 16px 0 4px;
}

/* Image cropper overlay */
#profile-image-cropper {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
}

/* ── Scrollbar ── */
.user-profile-page::-webkit-scrollbar       { width: 6px; }
.user-profile-page::-webkit-scrollbar-track { background: #f0f0f0; border-radius: 4px; }
.user-profile-page::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
.user-profile-page::-webkit-scrollbar-thumb:hover { background: #aaa; }

/* ── Responsive ── */
@media (max-width: 768px) {
  .user-profile-page { padding: 20px 12px 32px; gap: 16px; }
  .profile-card      { width: 100%; }
  .identifiers-card  { padding: 20px; }

  .card-header       { flex-direction: column; gap: 12px; text-align: center; }
  .identifier-tags   { flex-direction: column; }
  .identifier-tag    { width: 100%; min-width: unset; }
  .avatar-name       { font-size: 18px; }
}
</style>


// WEBPACK FOOTER //
// src/components/user-profile/UserProfile.vue